import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from 'lucide-react';
import {
  BMP_ACTIONS,
  bmpLogService,
  type BmpAction,
  type BmpConditions,
  type BmpLogEntry,
  type BmpLogEvent,
  type BmpTap,
} from '../../../services/bmpLogService';
import { formatDateOnlyNumeric } from '../../../utils/date';

/**
 * City of Austin Details (JIM-0919-09; reshaped 2026-09-29 as JIM-0929-01/02,
 * again 2026-10-03 as JIM-1003-06 and 2026-10-05 as JIM-1005-08/09/12/13).
 *
 * Its own section at the end of the report entry, Austin sites only: the
 * City of Austin notes first, then one compact card per BMP — its name, its
 * condition line and the five actions. The form shows the current state only:
 * what the last report recorded, carried forward, and what this visit says
 * about it. The history prints on the report, never here (Jim 2026-10-05:
 * "we just need to know the most recent item").
 *
 * One action per BMP per visit: tapping another replaces it, tapping the lit
 * one clears it, and nothing is recorded until the report is filed — "it's a
 * one-time selection, whatever you say is what it locks in". The condition
 * line follows the tap (JIM-1003-06) and is filed with the report too.
 *
 * Every card folds to one line — name, last action and date, condition — and
 * expands to edit; a BMP whose last recorded action was Removed starts folded
 * (Jim 2026-10-06: "shrinkable after being expanded… the status info when it
 * is collapsed and then editable when expanded"). One that has been on a sent
 * report can no longer be removed from the log.
 */
export function AustinBmpDetails({
  siteId,
  notes,
  setNotes,
  taps,
  setTaps,
  conditions,
  setConditions,
  reportDate,
  filedInspectionId = null,
}: {
  siteId: string;
  notes: string;
  setNotes: (v: string) => void;
  /** This visit's selection, at most one per BMP. */
  taps: BmpTap[];
  setTaps: (next: BmpTap[]) => void;
  /** Condition lines changed this visit, by BMP id. */
  conditions: BmpConditions;
  setConditions: (next: BmpConditions) => void;
  /** The report's date as entered at the top (YYYY-MM-DD); stamps this visit's selection. */
  reportDate: string;
  /**
   * Set while a filed inspection is reopened with Edit: its own event is
   * already in the log, and the selection restored with it would count twice.
   */
  filedInspectionId?: string | null;
}) {
  const queryClient = useQueryClient();
  const key = ['bmp-log', siteId];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => bmpLogService.list(siteId),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: () => bmpLogService.add(siteId, { bmp_description: 'New BMP' }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (entryId: string) => bmpLogService.remove(siteId, entryId),
    onSuccess: (_r, entryId) => {
      setTaps(taps.filter((t) => t.entry_id !== entryId));
      setConditions(without(conditions, entryId));
      invalidate();
    },
  });

  const rows = data ?? [];
  const tap = (entryId: string, action: BmpAction) => {
    const others = taps.filter((t) => t.entry_id !== entryId);
    const chosen = taps.find((t) => t.entry_id === entryId);
    if (chosen?.action === action) {
      // Tapping the lit action takes it back; the line returns to what the
      // last report filed.
      setTaps(others);
      setConditions(without(conditions, entryId));
      return;
    }
    setTaps([...others, { entry_id: entryId, action }]);
    setConditions({ ...conditions, [entryId]: labelFor(action) });
  };

  return (
    <section
      className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-teal-300 dark:border-teal-700 overflow-hidden"
      data-testid="austin-bmp-details"
    >
      <div className="px-4 py-3 bg-teal-50 dark:bg-teal-900/30 border-b border-teal-200 dark:border-teal-800">
        <h2 className="text-sm font-semibold text-teal-900 dark:text-teal-100">
          City of Austin Details
        </h2>
        <p className="text-xs text-teal-800/80 dark:text-teal-200/80 mt-0.5">
          One line per BMP. Tap what you saw; the report dates it.
        </p>
      </div>
      <div className="px-4 py-3 space-y-2">
        <label className="block">
          <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
            City of Austin notes
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            aria-label="City of Austin notes"
            placeholder="Notes for the City of Austin section of this report"
            className="mt-1 w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
          />
        </label>

        {isLoading ? (
          <p className="text-xs text-slate-400 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading BMPs…
          </p>
        ) : isError ? (
          <p className="text-xs text-danger-600">
            Could not load the BMP list — check the connection and try again.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            No BMPs recorded yet. Add the first one below.
          </p>
        ) : (
          rows.map((entry) => (
            <BmpCard
              key={entry.id}
              siteId={siteId}
              entry={entry}
              visitTap={taps.find((t) => t.entry_id === entry.id)}
              lastRecorded={
                entry.events.find(
                  (ev) =>
                    !filedInspectionId || ev.inspection_id !== filedInspectionId
                ) ?? null
              }
              reportDate={reportDate}
              condition={conditions[entry.id] ?? entry.detail ?? ''}
              onCondition={(text) =>
                setConditions({ ...conditions, [entry.id]: text })
              }
              onTap={(action) => tap(entry.id, action)}
              onSaved={invalidate}
              onRemove={() => remove.mutate(entry.id)}
              removing={remove.isPending}
            />
          ))
        )}
        <button
          type="button"
          onClick={() => add.mutate()}
          disabled={add.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          {add.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          Add BMP
        </button>
      </div>
    </section>
  );
}

function without(conditions: BmpConditions, entryId: string): BmpConditions {
  const rest = { ...conditions };
  delete rest[entryId];
  return rest;
}

function labelFor(action: string): string {
  return (
    BMP_ACTIONS.find((a) => a.action === action)?.label ??
    action.replace('_', ' ')
  );
}

const INPUT =
  'px-2 py-1 border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100';

function BmpCard({
  siteId,
  entry,
  visitTap,
  lastRecorded,
  reportDate,
  condition,
  onCondition,
  onTap,
  onSaved,
  onRemove,
  removing,
}: {
  siteId: string;
  entry: BmpLogEntry;
  /** What this visit says about the BMP, if anything yet. */
  visitTap: BmpTap | undefined;
  /** The newest event an earlier report recorded. */
  lastRecorded: BmpLogEvent | null;
  reportDate: string;
  /** The condition line as it stands in this visit. */
  condition: string;
  onCondition: (text: string) => void;
  onTap: (action: BmpAction) => void;
  onSaved: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  const [description, setDescription] = useState(entry.bmp_description);
  // Saving a name refetches the list a moment later; a name being typed in
  // another card (or this one) must not be put back to what the server has.
  const editingDescription = useRef(false);
  useEffect(() => {
    if (!editingDescription.current) setDescription(entry.bmp_description);
  }, [entry]);
  const save = useMutation({
    mutationFn: (patch: { bmp_description?: string }) =>
      bmpLogService.update(siteId, entry.id, patch),
    onSuccess: onSaved,
  });
  const commitDescription = () => {
    editingDescription.current = false;
    if (!description.trim()) {
      setDescription(entry.bmp_description);
      return;
    }
    if (description !== entry.bmp_description)
      save.mutate({ bmp_description: description });
  };

  const current = visitTap?.action ?? lastRecorded?.action ?? null;
  // A removed BMP takes no room until it is put back: folded to its name
  // (Jim 2026-10-05), and stays folded on every later visit.
  const [expanded, setExpanded] = useState(
    !(visitTap === undefined && current === 'removed')
  );
  const stateLine = visitTap
    ? `This visit: ${labelFor(visitTap.action)} ${formatDateOnlyNumeric(reportDate)}`
    : lastRecorded
      ? `Last report: ${lastRecorded.label} ${formatDateOnlyNumeric(lastRecorded.event_date)}`
      : 'Nothing recorded yet.';

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded={false}
        aria-label={`Expand ${entry.bmp_description}`}
        data-testid="austin-bmp-row"
        data-folded="true"
        className="w-full flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-600 px-2.5 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40"
      >
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
            {entry.bmp_description}
          </span>
          {condition && (
            <span className="block text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {condition}
            </span>
          )}
        </span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
          {stateLine.replace(/^(Last report|This visit): /, '')}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </button>
    );
  }

  return (
    <div
      className="rounded-lg border border-slate-200 dark:border-slate-600 p-2 space-y-1.5"
      data-testid="austin-bmp-row"
    >
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={description}
          placeholder="e.g. Silt fence at front of Lot 22"
          aria-label={`BMP and location for ${entry.bmp_description}`}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={() => {
            editingDescription.current = true;
          }}
          onBlur={commitDescription}
          className={`flex-1 min-w-0 text-sm font-medium ${INPUT}`}
        />
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-expanded
          aria-label={`Fold ${entry.bmp_description}`}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        {!entry.on_sent_report && (
          <button
            type="button"
            onClick={onRemove}
            disabled={removing}
            className="p-1.5 rounded-md text-slate-400 hover:text-danger-600 dark:hover:text-danger-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label={`Remove ${entry.bmp_description}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <input
        type="text"
        value={condition}
        placeholder="Condition or anything more about it"
        aria-label={`Detail for ${entry.bmp_description}`}
        onChange={(e) => onCondition(e.target.value)}
        className={`w-full text-xs ${INPUT}`}
      />
      <div
        className="flex flex-wrap gap-1"
        role="group"
        aria-label={`Record for ${entry.bmp_description}`}
      >
        {BMP_ACTIONS.map((a) => (
          <button
            key={a.action}
            type="button"
            onClick={() => onTap(a.action)}
            aria-pressed={current === a.action}
            aria-label={`${a.label} — ${entry.bmp_description}`}
            className={`px-2 py-1 rounded-full text-[11px] font-medium border ${
              current === a.action
                ? 'bg-teal-600 border-teal-600 text-white'
                : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>
      <p
        className="text-[11px] text-slate-500 dark:text-slate-400"
        data-testid="austin-bmp-state"
      >
        {save.isPending
          ? 'Saving…'
          : save.isError
            ? 'Not saved — try again'
            : stateLine}
      </p>
    </div>
  );
}
